import { useEffect } from 'react';
import {
  FloatingFormItem,
  InputDate,
  InputDecimal,
  InputText,
} from '@/components/_common';
import {
  useCreateMoreThanOneInstallment,
  useCreateOneInstallment,
  useUpdateOneInstallment,
} from '@/core/domain/invoice/Installment/Installment.hook';
import {
  CreateMoreThanOneInstallment,
  CreateOneInstallment,
  InstallmentFormValues,
  InstallmentModalEnum,
} from '@/core/domain/invoice/Installment/Installment.types';
import { DateTimeFormat } from '@/utils/moment';
import { Col, Form, Modal, Row } from 'antd';
import { useRouteMatch } from 'react-router-dom';
import { useGetOneBillingInvoice } from '@/core/domain/invoice/Installment/Installment.hook';
import moment from 'moment';

export interface Props {
  visible: boolean;
  onClose: () => void;
  modalType: InstallmentModalEnum;
  installmentSelected?: number;
  setModalType: (i: InstallmentModalEnum) => void;
}

const InvoiceInstallmentModal = ({
  visible,
  onClose,
  modalType,
  setModalType,
  installmentSelected,
}: Props) => {
  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;

  const [form] = Form.useForm<InstallmentFormValues>();

  const { mutateAsync: createOneMutateAsync, isLoading: isLoadingCreateOne } =
    useCreateOneInstallment();
  const {
    mutateAsync: createMoreThanOneMutateAsync,
    isLoading: isLoadingCreateMoreOne,
  } = useCreateMoreThanOneInstallment();
  const { mutateAsync: updateOneMutateAsync, isLoading: isLoadingUpdate } =
    useUpdateOneInstallment();
  const { mutateAsync: getOneMutateAsync, data } = useGetOneBillingInvoice();

  useEffect(() => {
    if (installmentSelected) {
      getOneMutateAsync({
        invoiceId: Number(invoiceId),
        id: Number(installmentSelected),
      });
    }
  }, [installmentSelected]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data, dueDate: moment(data.dueDate) });
    }
  }, [data, installmentSelected]);

  const handleOk = async () => {
    try {
      const formValues = await form.validateFields();

      if (modalType === InstallmentModalEnum.one || installmentSelected) {
        const objOne: CreateOneInstallment = {
          number: formValues.number.toString().padStart(3, '0'),
          dueDate: formValues.dueDate.format(DateTimeFormat),
          value: formValues.value,
          invoiceId: Number(invoiceId),
        };

        if (data?.id) {
          await updateOneMutateAsync({
            ...objOne,
            id: data.id,
          });
        } else {
          createOneMutateAsync(objOne);
        }
      }

      if (modalType === InstallmentModalEnum.moreThanOne) {
        const MoreThanOneObj: CreateMoreThanOneInstallment = {
          dueDateFirstInstallment:
            formValues.dueDateFirstInstallment.format(DateTimeFormat),
          installmentValue: Number(formValues.installmentValue),
          quantityInstallments: Number(formValues.quantityInstallments),
          invoiceId: Number(invoiceId),
        };

        await createMoreThanOneMutateAsync(MoreThanOneObj);
      }

      setModalType(InstallmentModalEnum.one);
      onClose();
    } catch (err) {}
  };

  return (
    <Modal
      confirmLoading={
        isLoadingCreateOne || isLoadingCreateMoreOne || isLoadingUpdate
      }
      destroyOnClose
      open={visible}
      onOk={() => handleOk()}
      onCancel={() => {
        setModalType(InstallmentModalEnum.one);
        form.resetFields();
        onClose();
      }}
      title="Dados para parcela"
    >
      <Form form={form} preserve={false}>
        <Row gutter={[16, 16]}>
          {modalType === InstallmentModalEnum.one || installmentSelected ? (
            <>
              <Col xs={24} md={12}>
                <FloatingFormItem name="number" label="Número da parcela">
                  <InputText
                    disabled={Boolean(installmentSelected)}
                    onlyNumber
                  />
                </FloatingFormItem>
              </Col>

              <Col xs={24} md={12}>
                <FloatingFormItem name="value" label="Valor da parcela">
                  <InputDecimal />
                </FloatingFormItem>
              </Col>

              <Col xs={24} md={12}>
                <FloatingFormItem name="dueDate" label="Data de vencimento">
                  <InputDate />
                </FloatingFormItem>
              </Col>
            </>
          ) : (
            <>
              <Col xs={24} md={12}>
                <FloatingFormItem
                  name="quantityInstallments"
                  label="Quant. parcelas"
                >
                  <InputText onlyNumber />
                </FloatingFormItem>
              </Col>

              <Col xs={24} md={12}>
                <FloatingFormItem
                  name="installmentValue"
                  label="Valor da parcela"
                >
                  <InputDecimal />
                </FloatingFormItem>
              </Col>

              <Col xs={24} md={12}>
                <FloatingFormItem
                  name="dueDateFirstInstallment"
                  label="Data de vencimento"
                >
                  <InputDate />
                </FloatingFormItem>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default InvoiceInstallmentModal;
